import {
  Lock,
  KeyRound,
  EyeOff,
  ServerCog,
  ShieldCheck,
  BadgeCheck,
} from "lucide-react";
import InfoPageLayout, { InfoSection } from "@/components/InfoPageLayout";

const PILLARS = [
  {
    icon: Lock,
    title: "End-to-end encryption",
    desc: "Health records, medications, and attachments are encrypted in transit (TLS 1.2+) and at rest (AES-256) on audited cloud infrastructure.",
  },
  {
    icon: KeyRound,
    title: "You control every share",
    desc: "Caregiver access is invitation-only and scoped by role. Revoking access removes visibility immediately across every device.",
  },
  {
    icon: EyeOff,
    title: "No ads. No data sales.",
    desc: "We don't run advertising, sell your data, or share it with third-party brokers. Your family's health is not a product.",
  },
  {
    icon: ServerCog,
    title: "HIPAA-aligned infrastructure",
    desc: "Built on providers with SOC 2 Type II attestations, role-based access controls, and audit logging for privileged operations.",
  },
];

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "AES-256 encryption" },
  { icon: BadgeCheck, label: "HIPAA-aligned practices" },
  { icon: Lock, label: "SOC 2 infrastructure" },
  { icon: EyeOff, label: "No data sold — ever" },
];

export default function Security() {
  return (
    <InfoPageLayout
      eyebrow="Security & Privacy"
      title="Health information deserves more than a password."
      intro="Security is the foundation of GuardianNest — not a feature we bolted on. This page describes the controls we have in place today and the shared responsibilities that keep your family's information safe."
      updated="July 2026"
    >
      <section>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              className="flex gap-4 rounded-2xl border border-border/60 bg-card p-5 shadow-sm"
              data-testid={`security-pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <p.icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground">{p.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <InfoSection title="Authentication">
        <p>
          Sign-in is protected with modern password hashing and short-lived session tokens. Optional
          phone verification adds a second factor for sensitive account actions like password reset,
          caregiver invitations, and profile deletion.
        </p>
      </InfoSection>

      <InfoSection title="Caregiver authorization">
        <p>
          Caregiver access is invitation-only. From Shared Access on your dashboard, invite a
          caregiver by email and choose their scope per profile — emergency info, medications,
          records, or full access. They must sign in with their own account to accept, and can only
          view the profiles you connect to them.
        </p>
        <p>
          Every request to view or change a record is checked server-side against row-level security
          rules that only allow the profile owner or an explicitly authorized caregiver. Roles
          include Co-Parent, Grandparent, Babysitter, School Nurse, and Teacher — each with a
          narrower scope than the last. Revoking a caregiver instantly ends their access on every
          device.
        </p>
      </InfoSection>

      <InfoSection title="How access decisions are enforced">
        <p>
          GuardianNest uses a defense-in-depth access model: every data table is protected by
          Row-Level Security (RLS) policies, and every sensitive permission check is performed by a
          server-side function that runs with elevated privileges only to verify the caller's
          identity and relationship to the data.
        </p>
        <p>
          These functions are marked{" "}
          <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-medium text-foreground">
            SECURITY DEFINER
          </code>{" "}
          so they can read the ownership and caregiver records needed to make a decision, but they
          still derive the caller's identity from their authenticated session — never from a
          parameter the caller supplies. For example, when the app checks whether someone can view a
          profile, it calls a function like{" "}
          <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-medium text-foreground">
            is_owner_or_caregiver
          </code>
          , which looks up the authenticated user ID and compares it to the profile owner or the
          approved caregivers list.
        </p>
        <p>
          Because the check happens inside the database, a client cannot bypass it by manipulating a
          request. The same pattern is used for admin checks, incoming invite lookups, caregiver
          grants, and audit logging. This is the mechanism that keeps the "you control every share"
          promise enforceable on the server, not just in the UI.
        </p>
      </InfoSection>

      <InfoSection title="How email delivery is protected">
        <p>
          Emails such as sign-in confirmations, caregiver invitations, and phone-verification codes
          are queued internally and processed by a single backend worker. The worker endpoint
          requires a valid signed JWT, and it explicitly rejects any token that is not issued for
          service-level operations.
        </p>
        <p>
          Queue reads are performed through a database wrapper that is only executable by
          authenticated callers. The worker itself runs with service-role credentials so it can
          update send logs and move failed messages to a dead-letter queue, but it cannot be
          triggered by an anonymous or ordinary user request. Rate-limit responses, authentication
          failures, and delivery errors are logged and retried safely, with a hard cap on attempts
          before a message is set aside for review.
        </p>
        <p>
          This design means that even if someone discovered the queue-worker's URL, they could not
          drain or re-queue messages without a valid service-role token issued by our backend.
        </p>
      </InfoSection>

      <InfoSection title="Data location and subprocessors">
        <p>
          GuardianNest runs on managed cloud infrastructure in the United States. Subprocessors are
          limited to the services we actually use to operate the app — hosting, database, email
          delivery, SMS delivery, and error monitoring — and each is bound by a data-processing
          agreement. A current list is available on request.
        </p>
      </InfoSection>

      <InfoSection title="Backups and retention">
        <p>
          Encrypted backups are taken on a regular rotation to protect against accidental loss. When
          you delete a record or close your account, we remove it from production systems within 30
          days and it ages out of backups on the standard rotation.
        </p>
      </InfoSection>

      <InfoSection title="Shared responsibility">
        <p>
          We protect the platform; you protect your account. Use a strong, unique password, enable
          phone verification, and only invite caregivers you personally trust. If a device is lost
          or an invited caregiver's status changes, revoke their access from Shared Access.
        </p>
      </InfoSection>

      <InfoSection title="Reporting a vulnerability">
        <p>
          Security researchers can reach us at{" "}
          <a
            href="mailto:security@guardiannest.net"
            className="font-semibold text-primary hover:underline"
            data-testid="security-vuln-email"
          >
            security@guardiannest.net
          </a>
          . We acknowledge reports within two business days and coordinate disclosure in good faith.
        </p>
      </InfoSection>

      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 rounded-2xl border border-border/60 bg-card px-6 py-4 shadow-sm">
        {TRUST_BADGES.map((b) => (
          <span
            key={b.label}
            className="flex items-center gap-2 font-heading text-xs font-semibold text-foreground sm:text-sm"
            data-testid={`security-badge-${b.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            <b.icon className="h-4 w-4 text-primary" />
            {b.label}
          </span>
        ))}
      </div>
    </InfoPageLayout>
  );
}
